<script>
  import { onMount } from 'svelte';
  import { page } from '$stores/pageStore';
  import { navigate as goto } from 'svelte-routing';
  import { format } from 'date-fns';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { faRotate } from '@fortawesome/pro-light-svg-icons';
  import StudyHeader from '$lib/StudyHeader.svelte';
  import StudyDetails from '$lib/StudyDetails.svelte';
  import FeatureInfo from '$lib/FeatureInfo.svelte';
  import Toolbar from '$lib/Toolbar.svelte';
  import Pagination from '$lib/Pagination.svelte';
  import Footer from '$lib/Footer.svelte';
  import { formatToLocalDateTime } from '$utils/dateUtils.js';
  import { selectedStudy } from '$stores/selectionStore';

  // Use local API endpoints instead of direct API with key
  const API_BASE_URL = '/api/proxy';

  let logs = [];
  let loading = true;
  let error = null;
  let totalPages = 1;
  let currentPage = 1;
  let pageSize = 20;
  let jobName = '';
  let eventType = '';
  let subjectId = '';
  let startDate = '';
  let endDate = '';
  let totalLogs = 0;

  // Event types are derived dynamically from SchedulerLog for the selected study
  let eventTypesPrimary = [];
  let eventTypesSecondary = [];
  let loadingEventTypes = false;
  let includeSecondary = false; // default: hide non-action/no-op events (skipped/no*/missing*)
  const SECONDARY_EVENT_TYPE_REGEX = /^(skipped($|[-_ ])|no($|[-_ ])|missing($|[-_ ]))/i;

  // Populated dynamically from SchedulerJobConfig for the selected study
  let jobNames = [];
  let loadingJobNames = false;
  let lastStudyId = null;
  let currentStudyId = null;

  onMount(() => {
    // Get filters from URL parameters
    const urlParams = $page.url.searchParams;

    // Safely parse URL parameters with defaults
    const urlJobName = urlParams.get('jobName');
    const urlEventType = urlParams.get('eventType');
    const urlSubjectId = urlParams.get('subjectId');
    const urlIncludeSecondary = urlParams.get('includeSecondary');
    const urlStartDate = urlParams.get('startDate');
    const urlEndDate = urlParams.get('endDate');
    const urlPage = urlParams.get('page');

    // Allow jobName from URL even before the dropdown list loads; we'll validate after loading configs
    if (urlJobName) {
      jobName = urlJobName;
    }

    if (urlEventType) {
      eventType = urlEventType;
    }

    if (urlSubjectId) {
      subjectId = urlSubjectId;
    }

    // Default: hide secondary events unless explicitly requested
    if (urlIncludeSecondary === 'true') {
      includeSecondary = true;
    } else if (urlEventType && SECONDARY_EVENT_TYPE_REGEX.test(urlEventType)) {
      // If someone deep-linked a secondary event type, show secondary by default so the filter works
      includeSecondary = true;
    }

    // Validate date format (YYYY-MM-DD)
    const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
    if (urlStartDate && dateRegex.test(urlStartDate)) {
      startDate = urlStartDate;
    }

    if (urlEndDate && dateRegex.test(urlEndDate)) {
      endDate = urlEndDate;
    }

    // Parse page number safely
    const parsedPage = parseInt(urlPage);
    if (!isNaN(parsedPage) && parsedPage > 0) {
      currentPage = parsedPage;
    }

    // Track selected study changes (runes-friendly, avoids legacy `$:` reactive statements).
    // The subscription fires immediately, so this also serves as the initial load.
    const unsubscribeStudy = selectedStudy.subscribe((study) => {
      currentStudyId = study?._id ?? null;
      if (currentStudyId === lastStudyId) return;

      lastStudyId = currentStudyId;
      (async () => {
        await fetchJobNames();
        await fetchEventTypes();
        // If URL provided a jobName that isn't configured for this study, clear it
        if (jobName && jobNames.length > 0 && !jobNames.includes(jobName)) {
          jobName = '';
          currentPage = 1;
          updateUrl();
        }
        // If selected eventType isn't valid for this study (or hidden by default), clear it
        const allEventTypes = [...eventTypesPrimary, ...eventTypesSecondary];
        if (eventType && !allEventTypes.includes(eventType)) {
          eventType = '';
          currentPage = 1;
          updateUrl();
        }
        if (!includeSecondary && eventType && SECONDARY_EVENT_TYPE_REGEX.test(eventType)) {
          eventType = '';
          currentPage = 1;
          updateUrl();
        }
        fetchLogs();
      })();
    });

    return () => unsubscribeStudy();
  });

  async function fetchJobNames() {
    loadingJobNames = true;
    try {
      if (!currentStudyId) {
        jobNames = [];
        jobName = '';
        return;
      }

      const queryParams = new URLSearchParams({
        page: 1,
        pageSize: 1000,
        sortField: 'jobId',
        sortOrder: 'asc',
        studyGUID: currentStudyId
      });

      const response = await fetch(`${API_BASE_URL}/schedulerJobConfigs?${queryParams}`);
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error fetching job configs for job dropdown:', errorText);
        throw new Error(`Failed to fetch job configs: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      const configs = Array.isArray(data?.data) ? data.data : [];

      // Scheduler logs use jobName; scheduler job configs store jobId. They should match.
      jobNames = Array.from(
        new Set(configs.map((c) => c?.jobId).filter((v) => typeof v === 'string' && v.length > 0))
      ).sort((a, b) => a.localeCompare(b));
    } catch (e) {
      console.error('Error building job dropdown list:', e);
      jobNames = [];
      // Don't block logs page if this fails — dropdown will just be empty
    } finally {
      loadingJobNames = false;
    }
  }

  async function fetchEventTypes() {
    loadingEventTypes = true;
    try {
      if (!currentStudyId) {
        eventTypesPrimary = [];
        eventTypesSecondary = [];
        return;
      }

      const queryParams = new URLSearchParams({
        studyGUID: currentStudyId
      });

      const response = await fetch(`${API_BASE_URL}/schedulerLogs/eventTypes?${queryParams}`);
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error fetching event types:', errorText);
        throw new Error(`Failed to fetch event types: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      eventTypesPrimary = Array.isArray(data?.data?.primary) ? data.data.primary : [];
      eventTypesSecondary = Array.isArray(data?.data?.secondary) ? data.data.secondary : [];
    } catch (e) {
      console.error('Error building event types list:', e);
      eventTypesPrimary = [];
      eventTypesSecondary = [];
    } finally {
      loadingEventTypes = false;
    }
  }

  async function fetchLogs() {
    loading = true;
    error = null;
    try {
      if (!currentStudyId) {
        logs = [];
        totalPages = 1;
        totalLogs = 0;
        throw new Error('Please select a study to view scheduler logs.');
      }

      const queryParams = new URLSearchParams({
        page: currentPage,
        pageSize,
        studyGUID: currentStudyId,
        ...(includeSecondary && { includeSecondary: 'true' }),
        ...(jobName && { jobName }),
        ...(eventType && { eventType }),
        ...(subjectId && { subjectId }),
        ...(startDate && { startDate }),
        ...(endDate && { endDate })
      });

      const response = await fetch(`${API_BASE_URL}/schedulerLogs?${queryParams}`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch logs: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      
      if (!data || !Array.isArray(data.data)) {
        console.error('Invalid data structure:', data);
        throw new Error('Invalid data structure in response');
      }
      
      logs = data.data;
      totalPages = data.pagination?.totalPages || 1;
      totalLogs = data.pagination?.total || 0;
    } catch (e) {
      error = e.message;
      console.error('Error fetching logs:', e);
      logs = [];
    } finally {
      loading = false;
    }
  }

  function updateUrl() {
    const url = new URL(window.location);
    
    // Only update URL parameters if they have values
    if (jobName) {
      url.searchParams.set('jobName', jobName);
    } else {
      url.searchParams.delete('jobName');
    }
    
    if (eventType) {
      url.searchParams.set('eventType', eventType);
    } else {
      url.searchParams.delete('eventType');
    }

    if (subjectId) {
      url.searchParams.set('subjectId', subjectId);
    } else {
      url.searchParams.delete('subjectId');
    }

    if (includeSecondary) {
      url.searchParams.set('includeSecondary', 'true');
    } else {
      url.searchParams.delete('includeSecondary');
    }
    
    if (startDate) {
      url.searchParams.set('startDate', startDate);
    } else {
      url.searchParams.delete('startDate');
    }
    
    if (endDate) {
      url.searchParams.set('endDate', endDate);
    } else {
      url.searchParams.delete('endDate');
    }
    
    // Always set page number
    url.searchParams.set('page', currentPage.toString());
    
    goto(url.toString(), { replaceState: true });
  }

  function handlePageChange(newPage) {
    currentPage = newPage;
    updateUrl();
    fetchLogs();
  }

  function handleFilter() {
    if (!includeSecondary && eventType && SECONDARY_EVENT_TYPE_REGEX.test(eventType)) {
      eventType = '';
    }
    currentPage = 1;
    updateUrl();
    fetchLogs();
  }

  function handleReset() {
    jobName = '';
    eventType = '';
    subjectId = '';
    startDate = '';
    endDate = '';
    currentPage = 1;
    updateUrl();
    fetchLogs();
  }

  function getEventTypeColor(type) {
    switch (type) {
      case 'started':
        return 'bg-blue-500';
      case 'completed':
        return 'bg-green-500';
      case 'error':
        return 'bg-red-500';
      case 'skipped':
        return 'bg-yellow-500';
      case 'reminder_created':
        return 'bg-purple-500';
      default:
        return 'bg-gray-500';
    }
  }
</script>

<StudyHeader />

<FeatureInfo 
  title="Scheduler Logs" 
  description="This page displays logs from the scheduler service, showing the execution history of various automated tasks. You can filter by job name, event type, and date range to find specific entries."
  marginBottom="mb-0"
/>

<StudyDetails showOptionsMenu={false} />

<!-- Title and Filters -->
<Toolbar title="Scheduler Logs" itemCountTitle="Logs" itemCount={totalLogs}>
  <div class="flex items-center gap-4">
    <div class="text-m mb-0">
      Job
      <select
        bind:value={jobName}
        on:change={handleFilter}
        class="h-6 w-32 ms-1 px-1 border rounded dark:bg-gray-700 dark:border-gray-600 text-sm text-gray-800 dark:text-gray-100"
      >
        <option value="">All Jobs</option>
        {#if loadingJobNames}
          <option value="" disabled>Loading...</option>
        {/if}
        {#each jobNames as name (name)}
          <option value={name}>{name}</option>
        {/each}
      </select>
    </div>

    <div class="text-m mb-0">
      Event Type
      <select
        bind:value={eventType}
        on:change={handleFilter}
        class="h-6 w-32 ms-1 px-1 border rounded dark:bg-gray-700 dark:border-gray-600 text-sm text-gray-800 dark:text-gray-100"
      >
        <option value="">All Events</option>
        {#if loadingEventTypes}
          <option value="" disabled>Loading...</option>
        {/if}
        {#each (includeSecondary ? [...eventTypesPrimary, ...eventTypesSecondary] : eventTypesPrimary) as type (type)}
          <option value={type}>{type}</option>
        {/each}
      </select>
    </div>

    <div class="text-m mb-0 flex items-center gap-2">
      <label class="flex items-center gap-1 cursor-pointer select-none">
        <input
          type="checkbox"
          bind:checked={includeSecondary}
          on:change={handleFilter}
          class="h-3 w-3"
        />
        <span class="text-xs text-gray-800 dark:text-gray-100">Include secondary</span>
      </label>
    </div>

    <div class="text-m mb-0">
      Subject ID
      <input
        type="text"
        bind:value={subjectId}
        on:change={handleFilter}
        placeholder="e.g. 004001"
        class="h-6 w-28 ms-1 px-1 border rounded dark:bg-gray-700 dark:border-gray-600 text-sm text-gray-800 dark:text-gray-100"
      />
    </div>

    <div class="text-m mb-0">
      Start Date
      <input
        type="date"
        bind:value={startDate}
        on:change={handleFilter}
        class="h-6 w-32 ms-1 px-1 border rounded dark:bg-gray-700 dark:border-gray-600 text-sm text-gray-800 dark:text-gray-100"
      />
    </div>

    <div class="text-m mb-0">
      End Date
      <input
        type="date"
        bind:value={endDate}
        on:change={handleFilter}
        class="h-6 w-32 ms-1 px-1 border rounded dark:bg-gray-700 dark:border-gray-600 text-sm text-gray-800 dark:text-gray-100"
      />
    </div>

    <button
      on:click={handleReset}
      class="h-6 px-2 text-sm border rounded dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-600"
    >
      Reset
    </button>

    <button
      on:click={fetchLogs}
      class="h-6 px-2 text-sm border rounded dark:bg-gray-700 dark:border-gray-600 text-gray-800 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-600"
    >
      <FontAwesomeIcon icon={faRotate} class="h-4 w-4" />
    </button>
  </div>
</Toolbar>

{#if loading}
  <div class="flex justify-center items-center h-64">
    <div class="loader"></div>
  </div>
{:else if error}
  <div class="text-red-500 text-center p-4">{error}</div>
{:else}
  <div class="px-1 pt-0">
    <table class="min-w-full border-collapse table-auto">
      <thead>
        <tr class="text-sm text-black dark:text-slate-300 bg-stone-200/50 dark:bg-gray-800/90">
          <th class="px-4 py-2 text-left bold">Timestamp</th>
          <th class="px-4 py-2 text-left bold">Job Name</th>
          <th class="px-4 py-2 text-left bold">Event Type</th>
          <th class="px-4 py-2 text-left bold">Environment</th>
          <th class="px-4 py-2 text-left bold">Instance</th>
          <th class="px-4 py-2 text-left bold">Details</th>
        </tr>
      </thead>
      <tbody>
        {#each logs as log, index (log?._id || `${log?.timestamp}-${log?.jobName}-${index}`)}
          <tr class="text-sm hover:bg-white dark:hover:bg-gray-775 text-gray-800 dark:text-slate-300 {index % 2 === 0 ? 'bg-stone-50 dark:bg-gray-725' : 'bg-stone-75 dark:bg-gray-750'}">
            <td class="px-4 py-2 whitespace-nowrap">
              {formatToLocalDateTime(log.timestamp, Intl.DateTimeFormat().resolvedOptions().timeZone)}
            </td>
            <td class="px-4 py-2 whitespace-nowrap">
              {log.jobName}
            </td>
            <td class="px-4 py-2 whitespace-nowrap">
              <span class="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset {getEventTypeColor(log.eventType)} text-white">
                {log.eventType}
              </span>
            </td>
            <td class="px-4 py-2 whitespace-nowrap">
              {log.environment || 'Unknown'}
            </td>
            <td class="px-4 py-2 whitespace-nowrap">
              <div class="flex flex-col">
                <span>{log.instanceId || 'Unknown'}</span>
                <span class="text-xs text-gray-500 dark:text-gray-400">{log.hostname || 'Unknown host'}</span>
              </div>
            </td>
            <td class="px-4 py-2">
              <pre class="whitespace-pre-wrap text-xs font-mono bg-gray-50 dark:bg-gray-800 p-2 rounded">{JSON.stringify(log.details, null, 2).trim()}</pre>
            </td>
          </tr>
        {/each}
      </tbody>
    </table>
  </div>
{/if}

<Footer>
  <Pagination {currentPage} {totalPages} onPageChange={handlePageChange} />
</Footer>